import { type Client, Client as ClientSchema } from '@uit/shared/schemas/client';
import {
  type ResourceListResponse,
  ResourceListResponse as ResourceListResponseSchema,
  isResourceComplete,
} from '@uit/shared/schemas/resource';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import {
  ExportResourcesDialog,
  type ExportResult,
} from '../components/resources/ExportResourcesDialog.js';
import { useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import s from './ResourcesListPage.module.scss';

const ClientList = z.array(ClientSchema);

const COMPANY_LABELS: Record<string, string> = {
  LLC: 'LLC',
  C_CORP: 'C-Corp',
  S_CORP: 'S-Corp',
};

// Sortable columns (T-149) — must match the API's resourceSortExpr allowlist.
type SortKey = 'name' | 'vendor' | 'type' | 'location' | 'client' | 'status';
type SortOrder = 'asc' | 'desc';

interface UrlParams {
  q: string;
  clientId: string;
  state: string;
  sort: SortKey;
  order: SortOrder;
  includeInactive: boolean;
  page: number;
}

function buildUrl({ q, clientId, state, sort, order, includeInactive, page }: UrlParams): string {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (clientId) params.set('client_id', clientId);
  if (state.trim()) params.set('state', state.trim());
  params.set('sort', sort);
  params.set('order', order);
  if (includeInactive) params.set('include_inactive', 'true');
  params.set('page', String(page));
  return `/api/resources?${params.toString()}`;
}

function SortHeader({
  label,
  col,
  sort,
  order,
  onSort,
}: {
  label: string;
  col: SortKey;
  sort: SortKey;
  order: SortOrder;
  onSort: (col: SortKey) => void;
}) {
  const active = sort === col;
  return (
    <th scope="col" aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={s.sortBtn} onClick={() => onSort(col)}>
        {label}
        <span className={s.sortArrow} aria-hidden="true">
          {active ? (order === 'asc' ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

export function ResourcesListPage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [state, setState] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [order, setOrder] = useState<SortOrder>('asc');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [exported, setExported] = useState<ExportResult | null>(null);
  // The CSV export is owner-only (T-186) — it is a bulk extract of every
  // resource including both rate columns. Server-enforced via requireOwner;
  // hiding the button just avoids handing a non-owner a guaranteed 403.
  const { admin } = useAuth();
  const isOwner = !!admin?.is_owner;

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Any filter or sort change resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, clientId, state, sort, order, showInactive]);

  // Click a header: toggle direction on the active column, else switch to it (asc).
  function onSort(col: SortKey): void {
    if (sort === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(col);
      setOrder('asc');
    }
  }

  const { data: clients } = useApi<Client[]>('/api/clients', ClientList);
  const { data, error, loading } = useApi<ResourceListResponse>(
    buildUrl({ q: debouncedQ, clientId, state, sort, order, includeInactive: showInactive, page }),
    ResourceListResponseSchema,
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1 className={s.title}>Resources</h1>
          <p className={s.subtitle}>
            Onboarded resources, their vendor, and their current engagement.
          </p>
        </div>
        {isOwner && (
          <button type="button" className={s.exportBtn} onClick={() => setExportOpen(true)}>
            Export CSV
          </button>
        )}
      </div>

      {exported && (
        <div className={s.exportBanner} role="status">
          <span>
            Downloaded <span className={s.exportFile}>{exported.filename}</span> ·{' '}
            {exported.rows} rows · {exported.columns} columns
            {exported.withRates && ' · incl. rates'}
          </span>
          <button
            type="button"
            className={s.exportBannerX}
            onClick={() => setExported(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {isOwner && (
        <ExportResourcesDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          resourceTotal={data?.total ?? 0}
          onExported={setExported}
        />
      )}

      <div className={s.filters}>
        <input
          type="search"
          className={s.search}
          placeholder="Search resource, vendor, or city…"
          aria-label="Search resources"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className={s.filterSelect}
          aria-label="Filter by client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        >
          <option value="">All clients</option>
          {clients?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          className={s.filterState}
          placeholder="State"
          aria-label="Filter by state"
          maxLength={2}
          value={state}
          onChange={(e) => setState(e.target.value.toUpperCase())}
        />
        <label className={s.toggleInactive}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {error && <div className={s.error}>Failed to load resources: {error.message}</div>}
      {!error && loading && !data && <div className={s.loading}>Loading resources…</div>}

      {data && data.items.length === 0 && (
        <div className={s.empty}>
          {debouncedQ || clientId || state
            ? 'No resources match those filters.'
            : 'No resources yet. They appear here once an onboarding reaches active.'}
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <table className={s.table}>
            <thead>
              <tr>
                <SortHeader label="Resource" col="name" sort={sort} order={order} onSort={onSort} />
                <SortHeader label="Vendor" col="vendor" sort={sort} order={order} onSort={onSort} />
                <SortHeader label="Type" col="type" sort={sort} order={order} onSort={onSort} />
                <SortHeader
                  label="Location"
                  col="location"
                  sort={sort}
                  order={order}
                  onSort={onSort}
                />
                <SortHeader label="Client" col="client" sort={sort} order={order} onSort={onSort} />
                <SortHeader label="Status" col="status" sort={sort} order={order} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const location = [r.business_address_city, r.business_address_state]
                  .filter(Boolean)
                  .join(', ');
                return (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/resources/${r.id}`} className={s.nameLink}>
                        {r.resource_name}
                      </Link>
                      {!r.active && <span className={s.badgeInactive}>Inactive</span>}
                      {!isResourceComplete(r) && <span className={s.incomplete}>Incomplete</span>}
                      {r.resource_email && <div className={s.subEmail}>{r.resource_email}</div>}
                    </td>
                    <td>
                      <Link to={`/vendors/${r.vendor_id}`} className={s.nameLink}>
                        {r.business_name}
                      </Link>
                    </td>
                    <td>
                      {r.company_type ? (
                        (COMPANY_LABELS[r.company_type] ?? r.company_type)
                      ) : (
                        <span className={s.muted}>—</span>
                      )}
                    </td>
                    <td>{location || <span className={s.muted}>—</span>}</td>
                    <td>{r.client_name ?? <span className={s.muted}>—</span>}</td>
                    <td>
                      {r.engagement_status ? (
                        <span className={s.badge}>{r.engagement_status}</span>
                      ) : (
                        <span className={s.muted}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={s.pagination}>
            <button
              type="button"
              className={s.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className={s.pageInfo}>
              {`Page ${data.page} of ${totalPages} · ${data.total} total`}
            </span>
            <button
              type="button"
              className={s.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
